// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.
// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { useEffect, useState } from 'react'

import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { PieChartPro } from '@mui/x-charts-pro'
import { PieChart } from '@mui/x-charts/PieChart'
import { useOtChainState } from '@react/redux/store'
import { REPORT_SIERRA_RESERVES } from '@react/reporting/ReportDescriptors'
import { ReportRowGeneratorClass } from '@react/reporting/ReportRowGeneratorClass'
import { erc20WeiToCommaDecString } from '@shared/conversions'
import { TransparencyVariant } from '@shared/Enums'
import { LytReservesCTO } from '@shared/LytReservesCTO'
import { LytReservesTable } from './components/LytReservesTable'
import { poolOffchainDataFetch } from '@react/fetchers/PoolFetchers'
import { PoolOffchainDataCTO } from '@shared/PoolOffchainDataCTO'
import { ReportExplorer } from '@react/reporting/ReportExplorer'
import { useReserves } from '@react/hooks/useCurrentAPY'

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff7f', '#ff69b4']

export function LytReserves(props: { variant?: TransparencyVariant }): React.ReactElement {
  const [, forceRerender] = useState({})
  const variant = props.variant ?? TransparencyVariant.ADMIN
  const isApp = variant === TransparencyVariant.APP
  const [reservesPieData, setReservesPieData] = useState<{ id: number; value: number; color: string }[]>([])
  const [poolOffchainData, setPoolOffchainData] = useState<PoolOffchainDataCTO | undefined>(undefined)

  const { reserves, reservesWithRates, isLoading } = useReserves({
    enabled: true,
  })
  const {
    chainConfiguration: { providerReadOnly },
    stageConfiguration: { serverApiBaseUrl, restAPIKey, lytPoolAddr },
  } = useOtChainState()

  const theme = useTheme()
  const [reportRowGeneratorClass, setReportRowGeneratorClass] = useState<ReportRowGeneratorClass | undefined>(undefined)

  useEffect(() => {
    if (lytPoolAddr && providerReadOnly) {
      const _reportRowGeneratorClass = new ReportRowGeneratorClass({
        reportDescriptor: REPORT_SIERRA_RESERVES,
        providerReadOnly,
        serverApiBaseUrl,
        restAPIKey,
        poolAddr: lytPoolAddr,
        lenderAddr: undefined,
      })
      setReportRowGeneratorClass(_reportRowGeneratorClass)
      _reportRowGeneratorClass.refreshData().then(() => {
        forceRerender({})
      })
    }
  }, [lytPoolAddr])

  useEffect(() => {
    if (reserves.length === 0) {
      return
    }

    const totalAssetValue = reserves.reduce((sum, reserve) => {
      return sum + parseFloat(erc20WeiToCommaDecString(reserve.currentAssetValue))
    }, 0)

    const pieData = reserves.map((reserve: LytReservesCTO, index: number) => {
      if (isApp) {
        const assetValue = parseFloat(erc20WeiToCommaDecString(reserve.currentAssetValue))
        const percentage = totalAssetValue > 0 ? (assetValue / totalAssetValue) * 100 : 0
        return {
          id: index,
          value: percentage,
          color: COLORS[index % COLORS.length],
        }
      } else {
        return {
          id: index,
          value: Number(reserve.percentReservesMax) / 100,
          label: reserve.poolName,
        }
      }
    })

    setReservesPieData(pieData as any)
  }, [reserves, isApp])

  // Fetch pool offchain data for yield type
  useEffect(() => {
    if (!serverApiBaseUrl || !restAPIKey || !lytPoolAddr) {
      return
    }

    poolOffchainDataFetch(serverApiBaseUrl, restAPIKey, lytPoolAddr)
      .then((offchainData: PoolOffchainDataCTO) => {
        setPoolOffchainData(offchainData)
      })
      .catch(() => {
        // Set default offchain data if fetch fails
        setPoolOffchainData({
          poolAddrOffchain: lytPoolAddr,
          yieldType: '',
          timestampOffchain: 0,
          benchmarkRate: '',
          transferInDays: 0,
          transferOutDays: 0,
          advisoryFeeRate: 0,
          platformFeeRate: 0,
          liquidityFeeRate: 0,
          contractualCurrency: '',
          minimumTransferAmount: 0,
          maximumTransferAmount: 0,
          earlyWithdrawalProcessingPeriod: 0,
          automaticRollover: false,
          rolloverCollateral: '',
          collateral: [],
        })
      })
  }, [serverApiBaseUrl, restAPIKey, lytPoolAddr])

  if (!isApp) {
    return (
      <Box
        data-testid="sierra-reserves-page"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          padding: '24px',
          backgroundColor: theme.palette.background.canvas,
        }}
      >
        <Box
          sx={{
            width: '70%',
            height: '100%',
          }}
        >
          {reportRowGeneratorClass && <ReportExplorer reportRowGeneratorClass={reportRowGeneratorClass} />}
        </Box>
        <Box
          className="reserves-pie-tooltip-scope"
          sx={{
            width: '30%',
            height: '100%',
            color: '#fff',
          }}
        >
          <PieChartPro
            slotProps={{
              legend: {
                direction: 'horizontal',
                position: {
                  horizontal: 'center',
                  vertical: 'bottom',
                },
              },
            }}
            series={[
              {
                innerRadius: 60,
                outerRadius: 80,
                data: reservesPieData,
              },
            ]}
            width={200}
            height={200}
          />
        </Box>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Box
        data-testid="sierra-reserves-page"
        sx={{
          backgroundColor: '#1a1a1a',
          padding: 2,
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.12)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress sx={{ color: '#d95f80' }} />
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Loading reserves data...
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      data-testid="sierra-reserves-page"
      sx={{
        backgroundColor: '#1a1a1a',
        padding: 2,
        borderRadius: 2,
        border: '1px solid rgba(255, 255, 255, 0.12)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: '50%',
            height: '100%',
          }}
        >
          <LytReservesTable
            reserves={(() => {
              const finalReserves = reservesWithRates.length > 0 ? reservesWithRates : reserves
              console.warn('LytReserves passing to table:', {
                reservesLength: reserves.length,
                reservesWithRatesLength: reservesWithRates.length,
                usingReservesWithRates: reservesWithRates.length > 0,
                sampleReserve: finalReserves[0]
                  ? {
                      poolName: finalReserves[0].poolName,
                      displayName: (finalReserves[0] as any).displayName,
                      yieldType: (finalReserves[0] as any).yieldType,
                    }
                  : null,
              })
              return finalReserves
            })()}
            poolOffchainData={poolOffchainData}
          />
        </Box>
        <Box
          sx={{
            width: '50%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: 4, // Add top padding to align title with reserves table
            pb: 4, // Add bottom padding for balance
          }}
        >
          <Typography variant="h6" sx={{ color: 'white', mb: 3, textAlign: 'center' }}>
            Reserve Distribution
          </Typography>
          <Box sx={{ position: 'relative', width: 400, height: 400 }}>
            <PieChart
              series={[
                {
                  data: reservesPieData,
                  innerRadius: 70,
                  outerRadius: 120,
                  paddingAngle: 2,
                  cornerRadius: 3,
                  valueFormatter: (value) => `${value.value.toFixed(1)}%`,
                },
              ]}
              width={400}
              height={400}
              margin={{ top: 50, bottom: 50, left: 50, right: 50 }}
            />
            {reservesPieData.map((entry, index) => {
              const totalValue = reservesPieData.reduce((sum, item) => sum + item.value, 0)
              const percentage = totalValue > 0 ? (entry.value / totalValue) * 100 : 0
              const cumulativeAngle = reservesPieData.slice(0, index).reduce((sum, item) => {
                const itemPercentage = totalValue > 0 ? (item.value / totalValue) * 100 : 0
                return sum + itemPercentage * 3.6
              }, 0)
              const angle = cumulativeAngle + (percentage * 3.6) / 2 - 90
              const radian = (angle * Math.PI) / 180
              const radius = 150
              const x = 200 + radius * Math.cos(radian)
              const y = 200 + radius * Math.sin(radian)

              return (
                <Typography
                  key={entry.id}
                  sx={{
                    position: 'absolute',
                    left: x - 15,
                    top: y - 8,
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '500',
                    textAlign: 'center',
                    width: 30,
                  }}
                >
                  {percentage.toFixed(1)}%
                </Typography>
              )
            })}
          </Box>
          <Box
            sx={{
              mt: 4,
              mb: 2,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              width: '100%',
              maxWidth: '400px',
              mx: 'auto',
            }}
          >
            {reserves.map((reserve, index) => (
              <Box key={reserve.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: COLORS[index % COLORS.length],
                    borderRadius: '50%',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '12px', lineHeight: 1.3 }}
                >
                  {reserve.poolName?.trim() || 'Liquidity Buffer (USDC)'}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
